"use client";

import clsx from "clsx";
import { Check } from "lucide-react";
import dynamic from "next/dynamic";
import DomPurify from "isomorphic-dompurify";
import Image from "components/Image";
import MaxWidthWrapper from "components/Tiles/commonComponents/MaxWidthWrapper";
import { getBlurUrl } from "components/Image/utils";
import DualButtonCTA, { ButtonVariant } from "components/ui/DualButtonCTA";
import FeaturedReview, { type FeaturedReviewProps } from "components/Tiles/TileComponents/FeaturedReview";
import { FadeIn, FadeInStagger } from "components/ui/FadeIn";
const MuxPlayer = dynamic(() => import("components/Tiles/commonComponents/MuxPlayer"));
import BeforeAfterCompare from "components/Tiles/TileComponents/BeforeAfter/BeforeAfterCompare";

type VerticalPadding = 'compact' | 'standard' | 'comfortable';

interface ButtonProps {
  buttonText: string;
  buttonUrl: string;
  buttonVariant: ButtonVariant;
}

type ListsContentProps = {
  bgColor?: string;
  compareImages?: {
    beforeImageUrl: string;
    afterImageUrl: string;
  };
  disableDash?: boolean;
  disclaimer?: string;
  miniTitle?: string;
  sectionWrapperClassName?: string;
  featuredReview?: FeaturedReviewProps;
  isRounded?: boolean;
  shouldPrioritize?: boolean;
  title?: string;
  oneTitle?: string;
  oneContent?: string;
  twoTitle?: string;
  twoContent?: string;
  content?: string;
  button1?: ButtonProps;
  button2?: ButtonProps;
  sectionTitle?: string;
  src?: string;
  videoId?: string;
  sections?: {
    content: string;
  }[];
  titleTag?: React.ElementType;
  imageContain?: boolean;
  useGifThumbnail?: boolean;
  verticalPadding?: VerticalPadding;
};

const ListsContent = ({
  bgColor,
  compareImages,
  disableDash = false,
  disclaimer,
  miniTitle,
  sectionWrapperClassName,
  featuredReview,
  isRounded = false,
  shouldPrioritize = false,
  title,
  oneTitle,
  oneContent,
  twoTitle,
  button1,
  button2,
  twoContent,
  content,
  sectionTitle,
  src,
  videoId,
  sections,
  titleTag = "h1",
  imageContain = false,
  useGifThumbnail,
  verticalPadding = 'standard'
}: ListsContentProps) => {
  const TitleTag = titleTag;
  const imageClassName = imageContain ? "object-contain" : "object-cover";

  const getVerticalPaddingClass = () => {
    switch (verticalPadding) {
      case 'compact':
        return 'py-12';
      case 'comfortable':
        return 'py-24';
      case 'standard':
      default:
        return 'py-16';
    }
  };

  const getInnerSpacingClass = () => {
    switch (verticalPadding) {
      case 'compact':
        return 'pb-8';
      case 'comfortable':
        return 'pb-20';
      case 'standard':
      default:
        return 'pb-16';
    }
  };

  return (
    <section
      style={{ backgroundColor: bgColor }}
      id="ListsContent-107"
      className={clsx("@container", sectionWrapperClassName)}
    >
      <MaxWidthWrapper className="my-0 px-8">
        <div className={clsx(
          getVerticalPaddingClass(),
          !disableDash && "lg:border-l-[1px] lg:border-dashed lg:border-gray-500"
        )}>
          <div className={clsx("grid w-full grid-cols-10 lg:gap-12", getInnerSpacingClass())}>
            <div className="col-span-10 h-full self-center px-0 lg:col-span-5 lg:pl-12">
              <div className="item-center h-full items-center justify-center self-center align-middle">
                {miniTitle && (
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-full  bg-[#f5f1e4] text-[#222] lg:-ml-20 lg:-mt-[44px] lg:mb-[15px] lg:inline-flex lg:h-16 lg:w-16">
                    <span className="text-[20px] font-normal text-[#222]">{miniTitle}</span>
                  </div>
                )}
                <div className="flex h-full">
                  <div className="self-center align-middle lg:-mt-[64px]">
                    <FadeIn>
                      <TitleTag
                        className="pb-6 font-heading text-[25px] leading-[1.2] lg:-mt-2 lg:mb-0  lg:text-[33px]"
                        dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(title || "") }}
                      />
                      {content && <p className="mt-4 text-base text-primary-700 lg:text-md">{content}</p>}
                    </FadeIn>

                    <FadeInStagger faster>
                      <div className="relative block min-h-[300px] w-full lg:col-span-5 lg:hidden lg:min-h-[550px]">
                        {videoId ? (
                          <div className="h-[500px] min-h-[500px]">
                            <MuxPlayer
                              playbackId={videoId}
                              loop
                              muted
                              maxResolution="2160p"
                              autoPlay
                              useGifThumbnail={useGifThumbnail}
                              className="border-primary-[#E8ECED] z-10  h-full w-full overflow-hidden rounded-xl border object-cover "
                            />
                          </div>
                        ) : compareImages ? (
                          <BeforeAfterCompare
                            beforeImageUrl={compareImages.beforeImageUrl}
                            afterImageUrl={compareImages.afterImageUrl}
                          />
                        ) : src ? (
                          <Image
                            alt={title || ""}
                            src={src}
                            className={clsx(imageClassName, isRounded && "rounded-xl")}
                            placeholder="blur"
                            blurDataURL={getBlurUrl()}
                            quality={70}
                            style={{
                              objectFit: "cover"
                            }}
                            priority={shouldPrioritize}
                            sizes="(max-width: 942px) 100vw, 50vw"
                            fill
                          />
                        ) : null}
                      </div>

                      {sectionTitle && <p className="py-2 text-base text-primary-700">{sectionTitle}</p>}

                      {!!sections?.length &&
                        sections.map((item, i) => {
                          return (
                            <ul
                              role="list"
                              key={i}
                              className="mt-[12px] space-y-2 text-base leading-6 text-primary-700 lg:max-w-[100%]"
                            >
                              <li>
                                <FadeIn className="flex items-center gap-x-3 overflow-hidden">
                                  <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#073937] [text-wrap:balance]">
                                    <Check color="#fff" className="shrink-0" strokeWidth={2.2} size={17} />
                                  </span>
                                  {item.content}
                                </FadeIn>
                              </li>
                            </ul>
                          );
                        })}
                      {oneTitle && oneContent ? (
                        <FadeIn className="flex items-center gap-x-4 px-4 pb-4 pt-8 lg:px-0">
                          <span className="flex gap-x-4 rounded-lg  bg-[#f5f1e4] px-4 py-2.5">
                            {oneTitle && oneContent ? (
                              <p className="text-base leading-none text-primary-700">
                                {oneTitle} <span className="pl-1 text-primary-700 ">{oneContent}</span>
                              </p>
                            ) : null}

                            {twoTitle && twoContent ? (
                              <p className="text-base leading-none text-primary-700 ">
                                {twoTitle} <span className="pl-1 text-primary-700 ">{twoContent}</span>
                              </p>
                            ) : null}
                          </span>
                        </FadeIn>
                      ) : null}
                      {disclaimer && (
                        <FadeIn>
                          <p className="lgmax-w-[95%]: max-w-[75%] px-4 pb-2 text-left text-sm text-[#717171] lg:px-0">
                            {disclaimer}
                          </p>
                        </FadeIn>
                      )}
                      {(button1 || button2) && (
                        <FadeIn>
                          <DualButtonCTA button1={button1} button2={button2} isLeftAligned={true} />
                        </FadeIn>
                      )}
                    </FadeInStagger>
                  </div>
                </div>
              </div>
            </div>
            <FadeIn className="relative hidden min-h-[450px] w-full items-center  justify-center self-center lg:col-span-5 lg:block lg:h-[500px]">
              {videoId ? (
                <div className="h-[450px] lg:h-[500px]">
                  <MuxPlayer
                    playbackId={videoId}
                    loop
                    muted
                    maxResolution="2160p"
                    autoPlay
                    useGifThumbnail={useGifThumbnail}
                    className="border-primary-[#E8ECED] z-10  h-full w-full overflow-hidden rounded-xl border object-cover "
                  />
                </div>
              ) : compareImages ? (
                <BeforeAfterCompare
                  beforeImageUrl={compareImages.beforeImageUrl}
                  afterImageUrl={compareImages.afterImageUrl}
                />
              ) : src ? (
                <Image
                  alt={title || ""}
                  src={src}
                  className={clsx(imageClassName, isRounded && "rounded-xl")}
                  placeholder="blur"
                  blurDataURL={getBlurUrl()}
                  quality={70}
                  style={{
                    objectFit: "cover"
                  }}
                  priority={shouldPrioritize}
                  sizes="(max-width: 942px) 100vw, 50vw"
                  fill
                />
              ) : null}
            </FadeIn>
          </div>
          {featuredReview && <FeaturedReview {...featuredReview} />}
        </div>
      </MaxWidthWrapper>
    </section>
  );
};

export default ListsContent;